const PAYLOAD_API_URL = process.env.NEXT_PUBLIC_PAYLOAD_API_URL;

/**
 * Fetch published blog posts with pagination.
 * @param {Object} options
 * @param {number} options.page - Page number (1-based)
 * @param {number} options.limit - Posts per page
 * @param {string} [options.category] - Filter by category slug
 * @returns {Promise<{docs: Array, totalDocs: number, totalPages: number, page: number}>}
 */
export async function getPosts({ page = 1, limit = 12, category } = {}) {
  const params = new URLSearchParams({
    "where[_status][equals]": "published",
    sort: "-publishedAt",
    limit: String(limit),
    page: String(page),
    depth: "1",
  });

  if (category) {
    params.append("where[categories.slug][equals]", category);
  }

  const res = await fetch(`${PAYLOAD_API_URL}/posts?${params.toString()}`);
  if (!res.ok) throw new Error(`Failed to fetch posts: ${res.status}`);
  return res.json();
}

/**
 * Fetch a single published post by slug.
 * @param {string} slug
 * @returns {Promise<Object|null>}
 */
export async function getPostBySlug(slug) {
  const params = new URLSearchParams({
    "where[slug][equals]": slug,
    "where[_status][equals]": "published",
    depth: "1",
  });

  const res = await fetch(`${PAYLOAD_API_URL}/posts?${params.toString()}`);
  if (!res.ok) throw new Error(`Failed to fetch post: ${res.status}`);
  const data = await res.json();
  return data.docs[0] || null;
}

/**
 * Fetch all published post slugs (for getStaticPaths).
 * @returns {Promise<string[]>}
 */
export async function getAllPostSlugs() {
  const params = new URLSearchParams({
    "where[_status][equals]": "published",
    limit: "1000",
    depth: "0",
  });

  const res = await fetch(`${PAYLOAD_API_URL}/posts?${params.toString()}`);
  if (!res.ok) throw new Error(`Failed to fetch post slugs: ${res.status}`);
  const data = await res.json();
  return data.docs.map((post) => post.slug);
}

/**
 * Fetch all categories.
 * @returns {Promise<Array>}
 */
export async function getCategories() {
  const res = await fetch(`${PAYLOAD_API_URL}/categories?limit=100&depth=0`);
  if (!res.ok) throw new Error(`Failed to fetch categories: ${res.status}`);
  const data = await res.json();
  return data.docs;
}

/**
 * Build the full URL for a Payload media item.
 * @param {Object|string} media - The media object (with url field) or URL string
 * @returns {string}
 */
export function getMediaUrl(media) {
  if (!media) return "";
  const url = typeof media === "string" ? media : media.url;
  if (url.startsWith("http")) return url;
  const serverUrl =
    process.env.NEXT_PUBLIC_PAYLOAD_API_URL?.replace("/api", "") || "";
  return `${serverUrl}${url}`;
}
